import {
  ValueAdjustmentGridConfig,
  ValueAdjustmentRowConfig,
} from 'src/app/module/adjustment-grid/interface/adjustment.interface';
import { PrimarySearchFilter, SecondarySearchFilter } from 'src/app/module/order/interface/order.interface';
import { PreviewSection } from 'src/app/module/order/interface/preview.interface';
import { PropertyAttribute } from 'src/app/module/property/interface/property.interface';

export type CompanySetting = { [reportSetting: string]: ReportSetting } & {
  additionalSettings: AdditionalSettings;
  adjustmentRecommendationSettings: AdjustmentRecommendationSettings;
  certification: Certification;
  companyKey: string;
  compsTableSettings?: string[] | null;
  costApproachTableSettings?: string[] | null;
  createdDate: string;
  defaultValues?: DefaultData;
  defaultSimilarityScoreWeights: Record<PropertyAttribute, number>;
  expiryInDays: number;
  googleMapId: string;
  id: string;
  imageSettings: ImageSettings;
  inspectionInfo: InspectionInfo;
  mcaRedirectOrderTypeList?: string[];
  orderIdType: string;
  outputAllComps: boolean;
  shortTermRentalCompsTableSettings?: string[] | null;
  supportConfigs: SupportConfigs;
  updatedDate: string;
  whiteLabelSettings: WhiteLabelSettings;
  showAppraisalAssist: boolean;
  showCreateOrderBtn?: boolean;
};

export interface ReportSetting {
  enabled: boolean;
  reportCategory: string;
  reportParent: string;
  reportDescription: string;
  avmProvider?: {
    enabled?: boolean;
  };
  initialRedirect: string;
  projectInformation: ProjectInformation;
  project?: ProjectPageSetting;
  clientInfo: ClientInfoSettings;
  salesComparison?: SalesComparisonApproachSetting;
  shortTermRental?: ShortTermRentalApproachSetting;
  longTermRental?: LongTermRentalApproachSetting;
  orderContext: OrderContextBarSettings;
  certificationPage: CertificationSettings;
  includeFloodData?: boolean;
  includeBuildingSketch?: boolean;
  hideFmv?: boolean;
  includeInspectionData?: boolean;
  includeEvalCharacteristics?: boolean;
  salesComparisonSettings?: string[] | null;
  reportDownloads?: string[] | null;
  reportForms?: string[] | null;
  reportExport?: ReportExport | ReportExport1 | ReportExport2;
  previewMenuConfig?: PreviewSection[];
  includeComments?: boolean;
  neighborhood?: NeighborhoodOrderPageSetting;
  reconciliation?: ReconciliationOrderPageSetting;
  costApproach?: CostApproachSetting;
  allowUnauditedDownload?: boolean;
  allowUnsignedDownload?: boolean;
  additionalCommentsConfig?: {
    enabled: boolean;
    commentConfigList: CommentConfig[];
  };
  reportConfiguration?: ReportConfiguration;
  primarySearchFilter?: PrimarySearchFilter;
  income?: ShortTermRentalIncomePageSetting;
  subject?: {
    enabled: boolean;
    moreDetailsButton?: {
      displayValue: string | null;
      enabled: boolean;
    };
  };
  draftOrder?: {
    allowPropertySearch?: boolean;
    propertySearchOptions?: DraftOrderSearchOptions;
    allowCustomSubject?: boolean;
  };
  orderSummary?: OrderSummary;
  additionalDocs: {
    enabled: boolean;
    supportedFileTypes?: string[];
  };
  preview: {
    enabled: boolean;
  };
  automation?: {
    enabled: boolean;
  };
  includeReviewValidation?: boolean;
  isCannedReport?: boolean;
  runReportLimit?: number;
  multipleScenario: {
    enabled: boolean;
    scenarioSelectionLimit: number;
  };
  reportDelivery?: {
    enabled: boolean;
  };
  showNotes?: {
    enabled: boolean;
  };
  allowRichTextFormatting: boolean;
  allowAddStructure?: boolean;
  allowAddUnit?: boolean;
  editSubjectPropertyAddress?: boolean;
  subjectCoordinateTolerance?: number;
  showSimilarityScore?: boolean;
  propertyImages?: {
    allowSettingCoverImage?: boolean;
    allowSettingSubjectCoverImage?: boolean;
    descriptionEnabledLabels?: Array<string>;
  };
  image?: { enabled: boolean; showGallery?: boolean };
  allowUnauditedSubmit?: boolean;
  reportSubmission?: { enabled: boolean };
  allowReportDownload?: boolean;
  comparableSearch?: {
    supportTotalBaths?: boolean;
    supportConditionQuality?: boolean;
  };
}

export interface BaseApproachSetting {
  enabled: boolean;
  valueAdjustmentLayout: ValueAdjustmentGridConfig;
  valueAdjustmentAdditionalFields: ValueAdjustmentRowConfig[];
  selectedComparableSortBy?: string;
  primarySearchFilter?: PrimarySearchFilter;
  secondarySearchFilter: SecondarySearchFilter;
  comments?: {
    enabled?: boolean;
    gridCommentSheet?: boolean;
    salesComparisonComment?: boolean;
    compComments?: boolean;
  };
  selection: {
    enabled: boolean;
  };
  adjustment: UnitAdjustmentSetting;
  valueAdjustment?: ValueAdjustment;
  allowCustomAttrGridHeader?: boolean;
  showAdditionalAttrsOnAddCustomProperty?: boolean;
  compMap?: {
    enabled: boolean;
  };
}

export interface UnitAdjustmentSetting {
  enabled: boolean;
  enableAdjustmentRecommendation: boolean;

  propmixAdjustment: { enabled: boolean; displayValue: string };
  regressionAdjustment: { enabled: boolean; displayValue: string };
  pairedSalesAdjustment: { enabled: boolean; displayValue: string };
  multiVariableRegressionAdjustment: { enabled: boolean; displayValue: string };
  unitAdjustment: { enabled: boolean; displayValue: string };

  showCompAdjustments?: boolean;
}

export interface SalesComparisonApproachSetting extends BaseApproachSetting {
  priorSale: {
    enabled: boolean;
    researchedPriorSalesYN: { enabled: boolean };
    subjectPriorSalesDataSource: { enabled: boolean };
    comparablePriorSalesDataSource: { enabled: boolean };
    researchedPriorSalesComments: { enabled: boolean };
  };
}

export interface CostApproachSetting extends BaseApproachSetting {
  building: {
    enabled: boolean;
  };
  land: {
    enabled: boolean;
  };
}

export interface ShortTermRentalApproachSetting extends BaseApproachSetting {
  income?: ShortTermRentalIncomePageSetting;
  value?: { enabled: boolean };
}

export interface ValueAdjustment {
  hideDerivedDailyRent?: boolean;
}

export interface LongTermRentalApproachSetting extends BaseApproachSetting {
  income?: LongTermRentalIncomePageSetting;
  value?: { enabled: boolean };
}

interface DefaultData extends ReportDefaultData {
  ALL: Record<string, unknown>;
}

type ReportDefaultData = Record<string, Record<string, unknown>>;

export interface ReportExport {
  enabled: boolean;
}

export interface WhiteLabelSettings {
  aliasName: string;
  propmixLabel: boolean;
  footer: string;
  companyLogo: string;
  verificationMail: string;
  enabled: boolean;
}

export interface ImageSettings {
  labelList?: string[] | null;
  selectAll: boolean;
  enabled: boolean;
}

export interface SupportConfigs {}

export interface ReportConfiguration {
  pdfTitle: string;
}

export interface CommentConfig {
  key: string;
  enabled: boolean;
  displayText: boolean;
  path?: string;
  scope?: CommentScope;
}

export type CommentScope = 'ORDER' | 'SCENARIO';

export interface ReportExport1 {
  enabled: boolean;
  artifact?: null;
  exportUrl?: null;
  authentication: Authentication;
}

export interface Authentication {
  scheme?: null;
  accessToken?: null;
  authUrl?: null;
  userName?: null;
  password?: null;
  grantType?: null;
  scope?: null;
}

export interface AdjustmentRecommendationSettings {
  propmixAdjustment: boolean;
  evalAdjustment: boolean;
  pairedSalesAdjustment: boolean;
  regressionAdjustment: boolean;
}

export interface AdditionalSettings {
  includeComments: boolean;
  includeMarketConditionComments: boolean;
  includeClientInfo: boolean;
  includeValuationComments: boolean;
  includeSupportingDocs: boolean;
  includeValidationSettings: boolean;
}

export interface ReportExport2 {
  enabled: boolean;
  artifact: string;
  exportUrl: string;
  authentication: Authentication1;
}

export interface Authentication1 {
  scheme: string;
  accessToken?: null;
  authUrl: string;
  userName: string;
  password: string;
  grantType?: null;
  scope?: null;
}

export interface NeighborhoodInsightSetting {
  enabled: boolean;
  singleIndicatorChart: SingleIndicatorChart[];
  groupIndicatorChart: GroupIndicatorChart[];
  marketField: MarketField[];
}

export enum SingleIndicatorChart {
  medianSoldPrice = 'medianSoldPrice',
  avgDOM = 'avgDOM',
  medianSoldPricePerSqft = 'medianSoldPricePerSqft',
  inventory = 'inventory',
  medianListPriceToSoldPrice = 'medianListPriceToSoldPrice',
  inventoryAbsorptionRate = 'inventoryAbsorptionRate',
  medianListPrice = 'medianListPrice',
}

export enum GroupIndicatorChart {
  priceTrend = 'priceTrend',
  // yearBuilt = 'yearBuilt',
  medianSoldToListPrice = 'medianSoldToListPrice',
  absorptionRate = 'absorptionRate',
  medianDOM = 'medianDOM',
  rentalIncome = 'rentalIncome',
  nightPrice = 'nightPrice',
  occupancy = 'occupancy',
}

export enum MarketField {
  boundary = 'boundary',
  description = 'description',
  isSimilarToNeighborhood = 'isSimilarToNeighborhood',
  propertyValue = 'propertyValue',
  demandSupply = 'demandSupply',
  marketingTime = 'marketingTime',
  exposureTime = 'exposureTime',
  location = 'location',
  buildUp = 'buildUp',
  growth = 'growth',
  employment = 'employment',
  population = 'population',
}

export interface InspectionInfo {
  inspectionProviders?: string[] | null;
  derivedFields?: string[] | null;
}

export interface Certification {
  enabled: boolean;
  certificationList?: string[];
  disclosureData?: string;
  includeDisclosure?: boolean;
}

export interface ReconciliationOrderPageSetting {
  enabled: boolean;
  repair: {
    enabled?: boolean;
    displayValue?: string | null;
    showInteriorSection?: boolean;
    showExteriorSection?: boolean;
    showConditionRating?: boolean;
    isSelectable: boolean;
    showRequiresAttentionField: boolean;
    showCommentField: boolean;
    showCostField: boolean;
    repairLineItems: [
      {
        key: string;
        type: 'interior' | 'exterior';
      },
    ];
    maxLimit?: number;
    allowCustomValue: boolean;
  };
  showCalculatedValue?: boolean;
}

export interface NeighborhoodOrderPageSetting {
  enabled: boolean;
  locality: {
    enabled: boolean;
    showInventorySheet?: boolean;
    showTrendsSheet?: boolean;
  };
  inventory: {
    enabled: boolean;
    comments?: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  trends: {
    enabled: boolean;
    salesTrends: NeighborhoodInsightSetting;
    shortTermRentalTrends: NeighborhoodInsightSetting;
  };
  allowInventorySearch?: boolean;
}

export interface ShortTermRentalIncomePageSetting {
  enabled: boolean;
  potentialGrossIncome: {
    enabled: boolean;
    displayValue: string | null;
    dailyRentValue: {
      enabled: boolean;
      displayValue: string | null;
    };
    calculatedDailyRent: {
      enabled: boolean;
      displayValue: string | null;
    };
    monthlyRentValue: {
      enabled: boolean;
      displayValue: string | null;
    };
    annualRentValue: {
      enabled: boolean;
      displayValue: string | null;
    };
    otherRevenue: {
      enabled: boolean;
      displayValue: string | null;
    };
    otherRevenueDescription: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  effectiveGrossIncome: {
    enabled: boolean;
    displayValue: string | null;
    occupancyRate: {
      enabled: boolean;
      displayValue: string | null;
    };
    nonOccupancyLoss: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  annualExpense: {
    enabled: boolean;
    displayValue: string | null;
    annualTax: {
      enabled: boolean;
      displayValue: string | null;
    };
    annualHoaFee: {
      enabled: boolean;
      displayValue: string | null;
    };
    managementCost: {
      enabled: boolean;
      displayValue: string | null;
    };
    insurance: { enabled: boolean; displayValue: string | null };
  };
  netOperatingIncome: { enabled: boolean; displayValue: string | null };
}

export interface LongTermRentalIncomePageSetting {
  enabled: boolean;
  potentialGrossIncome: {
    enabled: boolean;
    displayValue: string | null;

    calculatedMonthlyRent: {
      enabled: boolean;
      displayValue: string | null;
    };
    monthlyRentValue: {
      enabled: boolean;
      displayValue: string | null;
    };
    annualRentValue: {
      enabled: boolean;
      displayValue: string | null;
    };
    otherRevenue: {
      enabled: boolean;
      displayValue: string | null;
    };
    otherRevenueDescription: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  effectiveGrossIncome: {
    enabled: boolean;
    displayValue: string | null;
    vacancyRate: {
      enabled: boolean;
      displayValue: string | null;
    };
    vacancyLoss: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  annualExpense: {
    enabled: boolean;
    displayValue: string | null;
    annualTax: {
      enabled: boolean;
      displayValue: string | null;
    };
    annualHoaFee: {
      enabled: boolean;
      displayValue: string | null;
    };
    managementCost: {
      enabled: boolean;
      displayValue: string | null;
    };
    insurance: { enabled: boolean; displayValue: string | null };
  };
  netOperatingIncome: { enabled: boolean; displayValue: string | null };
}

export interface ClientInfoSettings {
  enabled: boolean;
  ownerName: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
  borrowerName: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
  clientName: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
  clientFileId: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
  intendedUseOfValuation: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
  assignmentObjective: {
    enabled: boolean;
    displayValue: string | null;
    isRequired: boolean;
  };
}

export interface OrderSummary {
  enabled: boolean;
  clientName: {
    enabled: boolean;
    displayValue: string | null;
  };
  clientFileId: {
    enabled: boolean;
    displayValue: string | null;
  };
  clientAddress: {
    enabled: boolean;
    displayValue: string | null;
  };
  ownerName: {
    enabled: boolean;
    displayValue: string | null;
  };
  borrowerName: {
    enabled: boolean;
    displayValue: string | null;
  };
  assignmentObjective: {
    enabled: boolean;
    displayValue: string | null;
  };
  intendedUseOfValuation: {
    enabled: boolean;
    displayValue: string | null;
  };
  effectiveDate: {
    enabled: boolean;
    displayValue: string | null;
  };
  inspectionInfo: {
    enabled: boolean;
    displayValue: string | null;
    inspectionSource: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectionOrderId: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectionDate: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectionType: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  inspectorInfo: {
    enabled: boolean;
    displayValue: string | null;
    inspectorType: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectorName: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectorPhone: {
      enabled: boolean;
      displayValue: string | null;
    };
    inspectorEmail: {
      enabled: boolean;
      displayValue: string | null;
    };
  };
  isRejected: {
    enabled: boolean;
    displayValue: string | null;
  };
  rejectionReasons: {
    enabled: boolean;
    displayValue: string | null;
  };
  rejectionDescription: {
    enabled: boolean;
    displayValue: string | null;
  };
  propertyFields?: PropertyAttribute[];
  intendedUseType?: { enabled: boolean; displayValue: string | null };
}

export interface OrderContextBarSettings {
  gla: {
    enabled: boolean;
    displayValue: string | null;
  };
  lotSize: {
    enabled: boolean;
    displayValue: string | null;
  };
  beds: {
    enabled: boolean;
    displayValue: string | null;
  };
  baths: {
    enabled: boolean;
    displayValue: string | null;
  };
  lastSale: {
    enabled: boolean;
    displayValue: string | null;
  };
  salesApproach: {
    enabled: boolean;
    displayValue: string | null;
  };
  costApproach: {
    enabled: boolean;
    displayValue: string | null;
  };
  longTermRental?: {
    enabled: boolean;
    displayValue: string | null;
  };
  shortTermRental: {
    enabled: boolean;
    displayValue: string | null;
  };
  profetEstimatedValue: {
    enabled: boolean;
    displayValue: string | null;
  };
  interactiveValue: {
    enabled: boolean;
    displayValue: string | null;
  };
  marketValue: {
    enabled: boolean;
    displayValue: string | null;
  };
  marketType: {
    enabled: boolean;
    displayValue: string | null;
  };
  location: {
    enabled: boolean;
    displayValue: string | null;
  };
  clientName: {
    enabled: boolean;
    displayValue: string | null;
  };
}

export interface CertificationSettings {
  enabled: boolean;
  certification: {
    enabled: boolean;
    displayValue: string;
    certificationList: string[];
    customCertification?: {
      enabled: boolean;
    };
  };
  disclosure: {
    enabled: boolean;
    displayValue: string;
    disclosureContent: string;
  };
  appraiserSignature: {
    enabled: boolean;
    displayValue: string | null;
  };
  companyName: {
    enabled: boolean;
    displayValue: string | null;
  };
  appraiserName: {
    enabled: boolean;
    displayValue: string | null;
  };
  licenseExpiry: {
    enabled: boolean;
    displayValue: string | null;
  };
  companyAddress: {
    enabled: boolean;
    displayValue: string | null;
  };
  licenseNumber: {
    enabled: boolean;
    displayValue: string | null;
  };
  contactPhone: {
    enabled: boolean;
    displayValue: string | null;
  };
  proximityToSubject?: {
    enabled: boolean;
    displayValue: string | null;
  };
  licensedSince?: {
    enabled: boolean;
    displayValue: string | null;
  };
  licenseTitle?: {
    enabled: boolean;
    displayValue: string | null;
  };
}

export interface DraftOrderSearchOptions {
  autoAddress?: boolean;
  address?: boolean;
  mls?: boolean;
  parcel?: boolean;
  owner?: boolean;
}

export interface ProjectPageSetting {
  enabled: boolean;

  /** When non-empty, the project page is shown only for these subject PropertySubType values. */
  visibleForPropertySubTypes?: string[];
}

export interface ProjectInformation {
  enabled: boolean;
  pud: {
    enabled: boolean;
    displayValue: string | null;
  };
  unitType: {
    enabled: boolean;
    displayValue: string | null;
  };
  legalName: {
    enabled: boolean;
    displayValue: string | null;
  };
  hoa: {
    enabled: boolean;
    displayValue: string | null;
  };
  phases: {
    enabled: boolean;
    displayValue: string | null;
  };
  totalUnits: {
    enabled: boolean;
    displayValue: string | null;
  };
  soldUnits: {
    enabled: boolean;
    displayValue: string | null;
  };
  forSaleUnits: {
    enabled: boolean;
    displayValue: string | null;
  };
  rentedUnits: {
    enabled: boolean;
    displayValue: string | null;
  };
  dataSourceUnitsInfo: {
    enabled: boolean;
    displayValue: string | null;
  };
  conversion: {
    enabled: boolean;
    displayValue: string | null;
  };
  conversionDate: {
    enabled: boolean;
    displayValue: string | null;
  };
  multiDwellingUnits: {
    enabled: boolean;
    displayValue: string | null;
  };
  multiDwellingDataSource: {
    enabled: boolean;
    displayValue: string | null;
  };
  recreationComplete: {
    enabled: boolean;
    displayValue: string | null;
  };
  status: {
    enabled: boolean;
    displayValue: string | null;
  };
  commonElementsLeased: {
    enabled: boolean;
    displayValue: string | null;
  };
  rentalItems: {
    enabled: boolean;
    displayValue: string | null;
  };
  commonElementsRecreation: {
    enabled: boolean;
    displayValue: string | null;
  };
}
